import {createApi} from '@reduxjs/toolkit/query/react';
import {baseQueryWithReAuthAndErrorHandling} from "@/store/features/baseRtkQuery";

export interface ConnectionStatus {
    provider: string;
    connectionId: string;
    isConnected: boolean;
    isWorking: boolean;
    message: string;
    error: string | null;
    testedAt: string;
}

export interface ProviderInfoResponse {
    name: string;
    displayName: string;
    description: string;
    supportsConnection: boolean;
    documentationUrl?: string;
    logoUrl?: string;
    requiresInstanceUrl: boolean;
    requiredScopes: string[];
    optionalScopes: string[];
    features: string[];
    supportsRefreshTokens: boolean;
    additionalProperties: Record<string, any>;
    instanceUrl?: string
}

export interface ConnectionRequest {
    instanceUrl?: string;
    returnUrl?: string;
}

export interface AuthUrl {
    authUrl: string;
}

export const integrationsApi = createApi({
    reducerPath: 'integrationsApi',
    baseQuery: baseQueryWithReAuthAndErrorHandling,
    tagTypes: ['Connection', 'Agents'],
    endpoints: (builder) => ({
        getConnectionStatus: builder.query<ConnectionStatus, string>({
            query: (integration: string) => ({
                url: `connections/${integration}/test`,
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
            }),
            providesTags: (_result, _error, integration) => [
                {type: 'Connection', id: integration}
            ],
        }),
        connectIntegration: builder.mutation<AuthUrl, { integration: string, connection: ConnectionRequest, requiresInstanceUrl: boolean }>({
            query: ({integration, connection, requiresInstanceUrl}) => ({
                url: `connections/${integration}/`,
                method: 'POST',
                body: requiresInstanceUrl? connection : {},
                headers: {
                    'Content-Type': 'application/json'
                },
            }),
            invalidatesTags: (_result, _error, {integration}) => [
                {type: 'Connection', id: integration},
                {type: 'Agents', id: 'LIST'}
            ],
        }),
        disconnectIntegration: builder.mutation<{ message: string }, string>({
            query: (integration: string) => ({
                url: `connections/${integration}/`,
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json'
                },
            }),
            invalidatesTags: (_result, _error, integration) => [
                {type: 'Connection', id: integration}
            ],
        }),
        getProviders: builder.query<ProviderInfoResponse[], void>({
            query: () => ({
                url: `connections/providers`,
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                },
            }),
        }),
        initialBoAdminEntraId: builder.mutation<boolean, { code: string, redirectUri: string }>({
            query: ({code, redirectUri}) => ({
                url: `connections/bo-admin-entra-id/initial`,
                method: 'POST',
                body: {
                    code,
                    redirectUri
                },
                headers: {
                    'Content-Type': 'application/json'
                },
            }),
            transformResponse: (response: any, meta: any) => {
                return meta?.response?.ok ?? false;
            }
        }),
    }),
});

export const {
    useGetConnectionStatusQuery,
    useConnectIntegrationMutation,
    useDisconnectIntegrationMutation,
    useGetProvidersQuery,
    useInitialBoAdminEntraIdMutation
} = integrationsApi;